import AssessmentIcon from '@mui/icons-material/Assessment';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Box,
    Button,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
    useTheme
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AthletePaymentAssignment, PurposeNames } from '../object-actions/types/types';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';
import { assignmentHasPurposeEntitlement, canAccessPreAssessment } from '../subscriptions/purposeEntitlements';
import { getOfferedPlan } from '../subscriptions/purchaseSteps';
import { getCoachContentForPurpose } from '../utils/assignmentPermissions';
import ContentScreenshotThumbnail from "./ContentScreenshotThumbnail";
import PreAssessmentCta from "./PreAssessmentCta";
import SubscriptionPurchaseCta from "./SubscriptionPurchaseCta";

const ProgramProgressStepper: React.FC<{ assignment: AthletePaymentAssignment }> = ({ assignment }) => {
    const theme = useTheme();
    const location = useLocation();
    const assignmentId = assignment.paymentassignment_id ?? assignment.id;
    // A trial only covers sessions 1-2, so the journey can run out of unlocked
    // content mid-program. Offer a membership instead of silently rendering
    // nothing.
    const offeredPlan = useMemo(
        () => getOfferedPlan(normalizeEntitlementsMap(assignment.entitlements)),
        [assignment.entitlements],
    );
    // State to track which purposes have their previous versions expanded
    const [expandedPurposes, setExpandedPurposes] = useState<Set<string>>(new Set());

    // Helper function to toggle expanded state for a purpose
    const toggleExpanded = (purpose: string) => {
        setExpandedPurposes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(purpose)) {
                newSet.delete(purpose);
            } else {
                newSet.add(purpose);
            }
            return newSet;
        });
    };

    const docs: Record<keyof typeof PurposeNames, any | null> = {} as any;
    for (const purpose of Object.keys(PurposeNames)) {
        docs[purpose as keyof typeof PurposeNames] = getCoachContentForPurpose(assignment, purpose as keyof typeof PurposeNames);
    }

    // Helper function to format date and time
    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Helper function to get the appropriate screenshot based on theme
    const getScreenshotUrl = (item: any) => {
        if (!item.entity) return null;

        // Use dark theme screenshot if available and theme is dark, otherwise use light
        const isDarkTheme = theme.palette.mode === 'dark';
        if (isDarkTheme && item.entity.screenshot_dark) {
            return item.entity.screenshot_dark;
        } else if (item.entity.screenshot_light) {
            return item.entity.screenshot_light;
        } else if (item.entity.screenshot_dark) {
            return item.entity.screenshot_dark;
        }

        return null;
    };

    // Helper function to get created date
    const getCreatedDate = (item: any) => {
        if (item.entity?.created_at) {
            return formatDateTime(item.entity.created_at);
        }
        return 'Date not available';
    };

    // Helper function to render content items
    const renderContentItems = (content: any[], purpose: string) => {
        if (!content || content.length === 0) return null;
        if (!assignmentHasPurposeEntitlement(assignment, purpose)) return null;

        const isExpanded = expandedPurposes.has(purpose);
        const hasMultipleVersions = content.length > 1;

        // Show only the latest entry (first in array) by default, or all if expanded
        const itemsToShow = isExpanded ? content : [content[0]];

        return (
            <Box>
                {itemsToShow.map((item, index) => {
                    const createdDate = getCreatedDate(item);
                    const screenshotUrl = getScreenshotUrl(item);
                    let reportTitle = PurposeNames[purpose as keyof typeof PurposeNames];
                    if (content.length > 1) {
                        reportTitle += ` #${item.id}`;
                    }

                    return (
                        <Box key={`${purpose}-${index}`} mb={2}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <ContentScreenshotThumbnail
                                    screenshotUrl={screenshotUrl}
                                    alt={`${reportTitle} preview`}
                                    data-attr="program-stepper_screenshot-thumbnail"
                                />

                                {/* Content details */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6">
                                        <Link
                                            to={`/${item._type === 'LessonPlan' ? 'lesson-plan' : 'coach-content'}/${item.id}`}
                                            data-attr={purpose}
                                            data-testid={purpose === 'lesson_plan' ? 'program-stepper--lesson-plan-link' : undefined}
                                        >View {reportTitle}</Link>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {createdDate}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}

                {/* Show Previous Versions button */}
                {hasMultipleVersions && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => toggleExpanded(purpose)}
                            sx={{ textTransform: 'none' }}
                        >
                            {isExpanded ? 'Hide Previous Versions' : `Show Previous Versions (${content.length - 1})`}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box>
            {offeredPlan && (
                <Box sx={{ mb: 2 }}>
                    <SubscriptionPurchaseCta
                        assignmentId={assignmentId}
                        productId={offeredPlan}
                        returnTo={location.pathname}
                        compact
                        membership={assignment.membership}
                    />
                </Box>
            )}

            <Stepper orientation="vertical" data-testid="program-stepper">
                {assignment.assessment_id &&
                    <Step expanded={true} data-testid="program-stepper--pre-assessment">
                        <StepLabel>Pre Assessment</StepLabel>
                        <StepContent>
                            <PreAssessmentCta assignment={assignment} />
                        </StepContent>
                    </Step>
                }
                <Step expanded={docs['feedback_report'] && docs['feedback_report'].length > 0} >
                    <StepLabel icon={<AssessmentIcon />}>Feedback Report</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['feedback_report'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'feedback_report');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Review detailed feedback and recommendations based on your assessment results.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                <Step expanded={docs['curriculum'] && docs['curriculum'].length > 0} >
                    <StepLabel icon={<LibraryBooksIcon />}>12 Session Curriculum</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['curriculum'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'curriculum');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Follow the structured 12-session program designed to enhance mental performance and resilience.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                <Step expanded={docs['lesson_plan'] && docs['lesson_plan'].length > 0} data-testid="program-stepper--lesson-plan" >
                    <StepLabel icon={<MenuBookIcon />}>Lesson Plan</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['lesson_plan'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'lesson_plan');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Access detailed lesson plans and resources for each session to maximize your learning experience.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                {assignment.pre_assessment_submitted_at && (
                    <>
                        <Step expanded={Boolean(docs['growth_report_week_4']?.length)} data-testid="program-stepper--growth-report-week-4">
                            <StepLabel icon={<TrendingUpIcon />}>Week 4 Growth Report</StepLabel>
                            <StepContent>
                                {(() => {
                                    const content = docs['growth_report_week_4'];
                                    if (content && content.length > 0) {
                                        return renderContentItems(content, 'growth_report_week_4');
                                    }
                                    return (
                                        <Typography variant="body2" color="text.secondary" data-testid="program-stepper--growth-report-week-4--empty">
                                            Your coach can generate this report after your pre-assessment is submitted.
                                        </Typography>
                                    );
                                })()}
                            </StepContent>
                        </Step>
                        <Step expanded={Boolean(docs['growth_report_week_8']?.length)} data-testid="program-stepper--growth-report-week-8">
                            <StepLabel icon={<TrendingUpIcon />}>Week 8 Growth Report</StepLabel>
                            <StepContent>
                                {(() => {
                                    const content = docs['growth_report_week_8'];
                                    if (content && content.length > 0) {
                                        return renderContentItems(content, 'growth_report_week_8');
                                    }
                                    return (
                                        <Typography variant="body2" color="text.secondary" data-testid="program-stepper--growth-report-week-8--empty">
                                            Your coach can generate this report after your pre-assessment is submitted.
                                        </Typography>
                                    );
                                })()}
                            </StepContent>
                        </Step>
                    </>
                )}

                {assignment.show_post_assessment_step &&
                    <Step expanded={true} data-testid="post-assessment-cta">
                        <StepLabel>Post Assessment</StepLabel>
                        <StepContent>
                            {assignment.post_assessment_submitted_at ? (
                                <Typography variant="body2" color="text.secondary">
                                    Completed
                                </Typography>
                            ) : !canAccessPreAssessment(assignment) ? (
                                <Typography variant="body2" color="text.secondary">
                                    Purchase the pre-assessment step to continue.
                                </Typography>
                            ) : (
                                <Button
                                    disabled={!assignment.can_submit_post_assessment}
                                    component={Link}
                                    to={`/assessments/${assignment.assessment_id}`}
                                    variant="contained"
                                    color='secondary'
                                    size={assignment.can_submit_post_assessment ? "large" : "small"}
                                >
                                    {assignment.post_submissions?.length > 0 ? "Resume Post-Assessment" : "Start Post-Assessment"}
                                </Button>
                            )}
                        </StepContent>
                    </Step>
                }

                {assignment.pre_assessment_submitted_at &&
                    <Step expanded={Boolean(docs['growth_report_week_12']?.length)} data-testid="program-stepper--growth-report">
                        <StepLabel icon={<TrendingUpIcon />}>Growth Report</StepLabel>
                        <StepContent>
                            {(() => {
                                const content = docs['growth_report_week_12'];
                                if (content && content.length > 0) {
                                    return renderContentItems(content, 'growth_report_week_12');
                                }
                                return (
                                    <Typography variant="body2" color="text.secondary" data-testid="program-stepper--growth-report--empty">
                                        Awaiting coach review.
                                    </Typography>
                                );
                            })()}
                        </StepContent>
                    </Step>
                }
            </Stepper>
        </Box>
    );
};

export default ProgramProgressStepper;
